// src/services/api.js
import axios from 'axios';
import authService from './auth';

const REACT_APP_API_URL = 'https://decko.fr/api';

// Configuration de base pour axios
const api = axios.create({
    baseURL: REACT_APP_API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true
});


// Services API

// Blog
export const blogService = {
    // Récupérer tous les articles
    getAll: async (params = {}) => {
        try {
            // Toujours faire un appel simple sans paramètres à /blog
            const response = await api.get('/blog');

            // Vérifier si nous avons reçu des données
            if (!response.data || !response.data.posts) {
                return { posts: [], pagination: { current_page: 1, per_page: 10, total_items: 0, total_pages: 1 } };
            }

            // Récupérer tous les articles
            let allPosts = response.data.posts || [];

            // Si la pagination indique qu'il y a plus de pages, récupérer toutes les pages
            const totalPages = response.data.pagination?.total_pages || 1;

            if (totalPages > 1) {
                try {
                    // Faire une deuxième requête pour récupérer la page 2 afin d'avoir tous les articles
                    const page2Response = await api.get('/blog?page=2');
                    if (page2Response.data && page2Response.data.posts) {
                        allPosts = [...allPosts, ...page2Response.data.posts];
                    }
                } catch (pageError) {
                    console.error("Erreur lors de la récupération de la page 2:", pageError);
                    // Continuer avec les articles déjà récupérés
                }
            }

            // Traitement côté client pour les filtres
            let filteredPosts = [...allPosts];

            // Filtrer par published si spécifié
            if (params.published !== undefined && params.published !== null) {
                filteredPosts = filteredPosts.filter(post =>
                    params.published === 1 || params.published === true
                        ? post.is_published === 1
                        : post.is_published === 0
                );
            }

            // Filtrer par catégorie si spécifiée
            if (params.category_id) {
                filteredPosts = filteredPosts.filter(post =>
                    post.category_id === parseInt(params.category_id)
                );
            }

            // Pagination
            const page = params.page || 1;
            const limit = params.limit || 10;
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

            return {
                posts: paginatedPosts,
                pagination: {
                    current_page: parseInt(page),
                    per_page: parseInt(limit),
                    total_items: filteredPosts.length,
                    total_pages: Math.ceil(filteredPosts.length / limit)
                }
            };
        } catch (error) {
            console.error("Erreur dans getAll:", error);
            throw error;
        }
    },

    // Récupérer un article par son slug
    getBySlug: async (slug) => {
        const response = await api.get(`/blog/${slug}`);
        return response.data;
    },

    // Récupérer les articles par catégorie
    getByCategory: async (categorySlug) => {
        const response = await api.get(`/blog/category/${categorySlug}`);
        return response.data;
    },

    // Ajouter un article (admin)
    create: async (postData) => {
        const response = await api.post('/blog', postData);
        return response.data;
    },

    // Mettre à jour un article (admin)
    update: async (id, postData) => {
        const response = await api.put(`/blog/${id}`, postData);
        return response.data;
    },

    // Supprimer un article (admin)
    delete: async (id) => {
        const response = await api.delete(`/blog/${id}`);
        return response.data;
    },
};

// Réalisations
// Version mise à jour de realizationsService
export const realizationsService = {
    // Récupérer toutes les réalisations
    getAll: async (params = {}) => {
        try {
            // Faire un appel simple sans paramètres à /realizations
            const response = await api.get('/realizations');

            // Vérifier si nous avons reçu des données
            if (!response.data || !response.data.realizations) {
                return { realizations: [], pagination: { current_page: 1, per_page: 10, total_items: 0, total_pages: 1 } };
            }

            // Récupérer toutes les réalisations
            let allRealizations = response.data.realizations || [];

            // Si la pagination indique qu'il y a plus de pages, récupérer toutes les pages
            const totalPages = response.data.pagination?.total_pages || 1;

            if (totalPages > 1) {
                try {
                    // Faire une deuxième requête pour récupérer la page 2 afin d'avoir toutes les réalisations
                    const page2Response = await api.get('/realizations?page=2');
                    if (page2Response.data && page2Response.data.realizations) {
                        allRealizations = [...allRealizations, ...page2Response.data.realizations];
                    }
                } catch (pageError) {
                    console.error("Erreur lors de la récupération de la page 2:", pageError);
                    // Continuer avec les réalisations déjà récupérées
                }
            }

            // Traitement côté client pour les filtres
            let filteredRealizations = [...allRealizations];

            // Filtrer par catégorie si spécifiée
            if (params.category_id) {
                filteredRealizations = filteredRealizations.filter(realization =>
                    realization.category_id === parseInt(params.category_id)
                );
            }

            // Pagination
            const page = params.page || 1;
            const limit = params.limit || 10;
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            const paginatedRealizations = filteredRealizations.slice(startIndex, endIndex);

            return {
                realizations: paginatedRealizations,
                pagination: {
                    current_page: parseInt(page),
                    per_page: parseInt(limit),
                    total_items: filteredRealizations.length,
                    total_pages: Math.ceil(filteredRealizations.length / limit)
                }
            };
        } catch (error) {
            console.error("Erreur dans getAll des réalisations:", error);
            throw error;
        }
    },

    // Récupérer une réalisation par son ID
    getById: async (id) => {
        const response = await api.get(`/realizations/${id}`);
        return response.data;
    },

    // Récupérer les réalisations par catégorie
    getByCategory: async (categorySlug) => {
        try {
            const response = await api.get(`/realizations/category/${categorySlug}`);

            // Vérifier le format de la réponse et l'adapter si nécessaire
            if (response.data && response.data.realizations) {
                return response.data;
            } else if (Array.isArray(response.data)) {
                // Si la réponse est un tableau, le convertir au format attendu
                return {
                    realizations: response.data,
                    category: null
                };
            }

            return response.data;
        } catch (error) {
            console.error("Erreur dans getByCategory:", error);
            throw error;
        }
    },

    // Ajouter une réalisation (admin)
    create: async (realizationData) => {
        const response = await api.post('/realizations', realizationData);
        return response.data;
    },

    // Mettre à jour une réalisation (admin)
    update: async (id, realizationData) => {
        const response = await api.put(`/realizations/${id}`, realizationData);
        return response.data;
    },

    // Supprimer une réalisation (admin)
    delete: async (id) => {
        const response = await api.delete(`/realizations/${id}`);
        return response.data;
    },
};

// Catégories
export const categoriesService = {
    // Récupérer toutes les catégories
    getAll: async () => {
        const response = await api.get('/categories');
        return response.data;
    },

    // Récupérer une catégorie par son slug
    getBySlug: async (slug) => {
        const response = await api.get(`/categories/${slug}`);
        return response.data;
    },

    // Ajouter une catégorie (admin)
    create: async (categoryData) => {
        const response = await api.post('/categories', categoryData);
        return response.data;
    },

    // Mettre à jour une catégorie (admin)
    update: async (id, categoryData) => {
        const response = await api.put(`/categories/${id}`, categoryData);
        return response.data;
    },

    // Supprimer une catégorie (admin)
    delete: async (id) => {
        const response = await api.delete(`/categories/${id}`);
        return response.data;
    },
};

// Contact
export const contactService = {
    // Envoyer un message de contact
    sendMessage: async (contactData) => {
        const response = await api.post('/contact', contactData);
        return response.data;
    },
};

export const uploadService = {
    // Uploader une image
    // Dans votre fichier JavaScript
    // Fonction simplifiée pour uploader une image
    uploadImage: async (file) => {
        try {
            // Créer un FormData pour envoyer le fichier
            const formData = new FormData();
            formData.append('image', file);

            // Appeler votre endpoint d'upload d'image
            const response = await axios.post(
                `${REACT_APP_API_URL}/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            // Retourner le nom du fichier uploadé
            if (response.data && response.data.filename) {
                return response.data.filename;
            }

            return null;
        } catch (error) {
            console.error('Erreur lors de l\'upload de l\'image:', error);
            return null;
        }
    }
};

export default api;